/**
 * The vocabulary of the Orders screen.
 *
 * The wire shapes come from `@elutide/shared` and are not restated here. What lives in
 * this file is what the screen calls things: the rail order, the labels, the chip sets and
 * the mapping from a server decided quick action to the words on its button.
 *
 * Nothing here derives a value. If a label depends on data, the data already carries the
 * answer: the payment method arrives with its own `method_label`, the row arrives with its
 * own `quick_action`, and this file only spells them.
 */
import type {
  OrderAttentionReason,
  OrderDrawerDetail,
  OrderDrawerLine,
  OrderPaymentState,
  OrderQuickAction,
  OrderQueue,
  OrderRange,
  OrdersPageKpis,
  OrdersPageResponse,
  OrdersPageRow,
  PaymentMethod,
  UnmatchedPaymentRow,
} from '@elutide/shared';

/* ------------------------------------------------------------------ */
/* Role projections                                                    */
/* ------------------------------------------------------------------ */

/**
 * What the screen can actually be handed, by role.
 *
 * `GET /orders/page`, `/orders/:id/detail` and `/orders/:id/timeline` are projected per role
 * on the server (`apps/api/src/modules/orders/domain/redact.ts`): a packer never receives
 * `kpis.paid_today`, `unmatched_payments`, a row's `total_cents`, `refunded_cents` or its
 * payment method and reference, and the drawer keeps only the payment state and deadline,
 * the line identities and quantities, and no lifetime block. An agent keeps the row money
 * and loses the two finance blocks. Removed means absent, never null and never zero.
 *
 * These types are the shared wire shapes with exactly those fields made optional, so the
 * owner response still satisfies them unchanged and every read of a projected field is a
 * compile error until it handles absence. They mirror the packer and agent types in
 * `redact.ts` rather than restating the field lists.
 */
type RowPayment = OrdersPageRow['payment'];
type RowPaymentHidden = 'method' | 'method_label' | 'reference';
type RowMoney = 'total_cents' | 'refunded_cents';

export type OrdersPageRowView = Omit<OrdersPageRow, RowMoney | 'payment'> &
  Partial<Pick<OrdersPageRow, RowMoney>> & {
    payment: Omit<RowPayment, RowPaymentHidden> & Partial<Pick<RowPayment, RowPaymentHidden>>;
  };

export type OrdersPageKpisView = Omit<OrdersPageKpis, 'paid_today'> &
  Partial<Pick<OrdersPageKpis, 'paid_today'>>;

export type OrdersPageResponseView = Omit<
  OrdersPageResponse,
  'kpis' | 'rows' | 'unmatched_payments'
> & {
  kpis: OrdersPageKpisView;
  rows: OrdersPageRowView[];
  unmatched_payments?: UnmatchedPaymentRow[];
};

type DrawerHeader = OrderDrawerDetail['header'];
type DrawerPayment = OrderDrawerDetail['payment'];
type DrawerItems = OrderDrawerDetail['items'];
type DrawerCustomer = OrderDrawerDetail['customer'];
type DrawerPaymentKept =
  | 'paid_at'
  | 'matcher_state'
  | 'autocancel_at'
  | 'autocancel_extendable'
  | 'reminder_eligible';
type DrawerLineKept = 'line_id' | 'product_id' | 'variation_id' | 'sku' | 'name' | 'qty';
type DrawerCustomerHidden = 'email' | 'phone' | 'billing' | 'lifetime' | 'rr_balance_cents';

export type OrderDrawerLineView = Pick<OrderDrawerLine, DrawerLineKept> &
  Partial<Omit<OrderDrawerLine, DrawerLineKept>>;

export type OrderDrawerDetailView = Omit<
  OrderDrawerDetail,
  'header' | 'payment' | 'items' | 'customer'
> & {
  header: Omit<DrawerHeader, 'total_cents'> & Partial<Pick<DrawerHeader, 'total_cents'>>;
  payment: Pick<DrawerPayment, DrawerPaymentKept> & Partial<Omit<DrawerPayment, DrawerPaymentKept>>;
  items: Omit<DrawerItems, 'lines' | 'subtotal_cents' | 'discount_cents' | 'shipping_cents' | 'tax_cents' | 'total_cents' | 'refunded_cents'> & {
    lines: OrderDrawerLineView[];
  } & Partial<Pick<DrawerItems, 'subtotal_cents' | 'discount_cents' | 'shipping_cents' | 'tax_cents' | 'total_cents' | 'refunded_cents'>>;
  customer: Omit<DrawerCustomer, DrawerCustomerHidden> & Partial<Pick<DrawerCustomer, DrawerCustomerHidden>>;
};


/* ------------------------------------------------------------------ */
/* Queues                                                              */
/* ------------------------------------------------------------------ */

export const QUEUE_LABELS: Readonly<Record<OrderQueue, string>> = {
  needs_attention: 'Needs attention',
  awaiting_payment: 'Awaiting payment',
  to_print: 'To print',
  to_pack: 'To pack',
  packed: 'Packed',
  in_transit: 'In transit',
  delivered: 'Delivered',
  exceptions: 'Exceptions',
  all: 'All',
};

/**
 * The rail, in spec order, and the order the number keys 1 to 8 select.
 *
 * `exceptions` is a queue the API can serve but not a chip on the rail: spec 02-orders
 * folds an open exception into Needs attention, and a rail with two doors onto the same
 * problem is a rail where one of them is always the wrong one.
 */
export const RAIL_QUEUES: readonly OrderQueue[] = [
  'needs_attention',
  'awaiting_payment',
  'to_print',
  'to_pack',
  'packed',
  'in_transit',
  'delivered',
  'all',
];

/** Legend id per rail queue, so the rail explains itself on hover. */
export const QUEUE_LEGEND_ID: Readonly<Partial<Record<OrderQueue, string>>> = {
  needs_attention: 'orders.needs_attention',
  awaiting_payment: 'orders.awaiting_payment',
  to_print: 'orders.to_print',
  to_pack: 'orders.to_pack',
  packed: 'orders.packed',
  in_transit: 'orders.in_transit',
  delivered: 'orders.delivered',
};

/* ------------------------------------------------------------------ */
/* Range                                                               */
/* ------------------------------------------------------------------ */

export const RANGE_CHIPS: readonly { id: OrderRange; label: string }[] = [
  { id: '7d', label: '7d' },
  { id: '30d', label: '30d' },
  { id: '90d', label: '90d' },
  { id: 'all', label: 'All' },
];

export const RANGE_WORDS: Readonly<Record<OrderRange, string>> = {
  '7d': 'the last 7 days',
  '30d': 'the last 30 days',
  '90d': 'the last 90 days',
  all: 'all time',
};

/* ------------------------------------------------------------------ */
/* Payment                                                             */
/* ------------------------------------------------------------------ */

/**
 * One glyph per method.
 *
 * Text, not an icon font and not an image: it renders at any size, it survives a copy and
 * paste into a note, and it needs no network. The label always sits beside it, because
 * colour and shape are never the only signal.
 */
export const PAYMENT_GLYPH: Readonly<Record<PaymentMethod, string>> = {
  card: 'CD',
  zelle: 'ZL',
  venmo: 'VN',
  cashapp: 'CA',
  comp: 'CP',
  store_credit: 'RR',
  offline_other: 'OF',
  unknown: '??',
};

export const PAYMENT_FILTERS: readonly { id: PaymentMethod; label: string }[] = [
  { id: 'card', label: 'Card' },
  { id: 'zelle', label: 'Zelle' },
  { id: 'venmo', label: 'Venmo' },
  { id: 'cashapp', label: 'Cash App' },
  { id: 'store_credit', label: 'Store credit' },
  { id: 'comp', label: 'Comp' },
  { id: 'offline_other', label: 'Other offline' },
  { id: 'unknown', label: 'Unmapped' },
];

export const PAYMENT_STATE_LABEL: Readonly<Record<OrderPaymentState, string>> = {
  paid: 'Paid',
  waiting: 'Waiting',
  failed: 'Failed',
  refunded: 'Refunded',
};

export const PAYMENT_STATE_TONE: Readonly<
  Record<OrderPaymentState, 'ok' | 'warn' | 'crit' | 'neutral'>
> = {
  paid: 'ok',
  waiting: 'warn',
  failed: 'crit',
  refunded: 'neutral',
};

export const MATCHER_LABEL: Readonly<Record<'auto' | 'sms' | 'manual', string>> = {
  auto: 'Auto-matched',
  sms: 'SMS-confirmed',
  manual: 'Manual',
};

/* ------------------------------------------------------------------ */
/* Attention                                                           */
/* ------------------------------------------------------------------ */

/** One chip per reason, in the words the spec uses for it. */
export const ATTENTION_LABEL: Readonly<Record<OrderAttentionReason, string>> = {
  compliance_hold: 'Compliance hold',
  address_flagged: 'Address flagged',
  attempt_cluster: 'Attempt cluster',
  writeback_dead: 'Write-back failed',
  payment_variance: 'Payment variance',
  unmapped_payment_method: 'Unmapped method',
  exception_open: 'Exception open',
  failed_first_order: 'Failed first order',
  stuck_awaiting_payment: 'Waiting too long',
  stuck_in_work: 'Stuck in work',
  stuck_packed: 'Packed, no scan',
};

/** Which section of the drawer a reason wants opened when Review is pressed. */
export const ATTENTION_SECTION: Readonly<Record<OrderAttentionReason, DrawerSection>> = {
  compliance_hold: 'flags',
  address_flagged: 'flags',
  attempt_cluster: 'flags',
  writeback_dead: 'activity',
  payment_variance: 'payment',
  unmapped_payment_method: 'payment',
  exception_open: 'flags',
  failed_first_order: 'payment',
  stuck_awaiting_payment: 'payment',
  stuck_in_work: 'fulfilment',
  stuck_packed: 'fulfilment',
};

export type DrawerSection =
  | 'payment'
  | 'fulfilment'
  | 'items'
  | 'customer'
  | 'activity'
  | 'flags';

/* ------------------------------------------------------------------ */
/* Actions                                                             */
/* ------------------------------------------------------------------ */

/**
 * The words on the one contextual button.
 *
 * The action itself is chosen by the server (`quick_action` on the row), so the button a
 * packer sees and the button the queue count implies cannot disagree. This map only turns
 * the server's answer into English.
 */
export const QUICK_ACTION_LABEL: Readonly<Record<OrderQuickAction, string>> = {
  mark_paid: 'Mark paid',
  print: 'Print',
  reprint: 'Reprint',
  track: 'Track',
  review: 'Review',
};

/* ------------------------------------------------------------------ */
/* Long tail filters                                                   */
/* ------------------------------------------------------------------ */

/**
 * The statuses behind the Filter button.
 *
 * The rail covers the pipeline; these are the ends an order can come to that no queue
 * shows, which is exactly what the spec asks the filter to carry.
 */
export const STATUS_FILTERS: readonly { id: string; label: string }[] = [
  { id: 'pending', label: 'Pending' },
  { id: 'on-hold', label: 'On hold' },
  { id: 'processing', label: 'Processing' },
  { id: 'completed', label: 'Completed' },
  { id: 'packed', label: 'Packed' },
  { id: 'shipped', label: 'Shipped' },
  { id: 'delivered', label: 'Delivered' },
  { id: 'cancelled', label: 'Cancelled' },
  { id: 'refunded', label: 'Refunded' },
  { id: 'failed', label: 'Failed' },
];

export const STATUS_LABEL: Readonly<Record<string, string>> = Object.fromEntries(
  STATUS_FILTERS.map((status) => [status.id, status.label]),
);
